<script lang="ts">
  import Button from '../../Button.svelte';
  import Input from '../../Input.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Notification from '../../Notification.svelte';
  import { employersService, type Employer } from '../../../lib/api/admin/employers.service';
  
  export let open = false;
  export let onClose: () => void;
  export let onSave: (data: any) => void;
  export let employer: any = null; // For edit mode
  
  // Employer fields
  let name = '';
  let abn = '';
  let contact_name = '';
  let contact_email = '';
  let contact_phone = '';
  let head_office_location = '';
  let status = 'unapproved'; // Default to unapproved
  
  // Form state
  let isEditMode = false;
  let loading = false;
  
  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';
  
  // Field errors
  let fieldErrors: Record<string, string[]> = {};
  
  // Track if the form has been populated with employer data
  let isFormPopulated = false;

  // When employer prop changes (for edit mode)
  $: if (employer && open) {
    isEditMode = true;
    // Only set initial values when first opening in edit mode
    // to avoid overwriting user edits
    if (!isFormPopulated) {
      populateFormWithEmployerData(employer);
      isFormPopulated = true;
    }
  } else if (open && !employer) {
    isEditMode = false;
    resetForm();
    isFormPopulated = false;
  }

  // Watch for changes to the open prop
  $: {
    if (open) {
      // If opening in edit mode, populate form if not already done
      if (employer && !isFormPopulated) {
        isEditMode = true;
        populateFormWithEmployerData(employer);
        isFormPopulated = true;
      } else if (!employer) {
        isEditMode = false;
        resetForm();
      }
    } else {
      // Reset form population flag when modal is closed
      isFormPopulated = false;
    }
  }
  
  // Status options
  const statusOptions = [
    { uuid: 'approved', name: 'Approved' },
    { uuid: 'unapproved', name: 'Unapproved' }
  ];

  // Function to populate form with employer data
  function populateFormWithEmployerData(employerData: any) {
    if (!employerData) return;
    
    name = employerData.name || '';
    abn = employerData.abn || '';
    contact_name = employerData.contact_name || '';
    contact_email = employerData.contact_email || '';
    contact_phone = employerData.contact_phone || '';
    head_office_location = employerData.head_office_location || '';
    status = employerData.status || 'unapproved';
  }
  
  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  function getFieldError(fieldName: string): string | undefined {
    const errors = fieldErrors[fieldName];
    return errors && errors.length > 0 ? errors[0] : undefined;
  }

  function handleFieldErrors(message: string) {
    showNotification(message, 'error');
  }

  function resetForm() {
    name = '';
    abn = '';
    contact_name = '';
    contact_email = '';
    contact_phone = '';
    head_office_location = '';
    status = 'unapproved';
    fieldErrors = {};
    loading = false;
  }

  function handleCancel() {
    resetForm();
    onClose();
  }

  async function handleSubmit() {
    loading = true;
    fieldErrors = {};
    
    // Basic client-side validation
    if (!name.trim()) {
      fieldErrors.name = ['Employer name is required'];
      loading = false;
      showNotification('Please fill in all required fields', 'error');
      return;
    }
    
    if (!abn.trim()) {
      fieldErrors.abn = ['ABN is required'];
      loading = false;
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    if (contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact_email)) {
      fieldErrors.contact_email = ['Please provide a valid email address'];
      loading = false;
      showNotification('Please provide a valid email address', 'error');
      return;
    }
    
    const employerData: Partial<Employer> = {
      name: name.trim(),
      abn: abn.trim(),
      contact_name: contact_name.trim() || null,
      contact_email: contact_email.trim(),
      contact_phone: contact_phone.trim() || null,
      head_office_location: head_office_location.trim() || null,
    };

    // Include status in requests (both create and update)
    (employerData as any).status = status;
    
    try {
      let response;
      
      if (isEditMode && employer?.uuid) {
        response = await employersService.updateEmployer(employer.uuid, employerData);
      } else {
        response = await employersService.createEmployer(employerData);
      }
      
      // Success case
      if (response.success) {
        showNotification(isEditMode ? 'Employer updated successfully' : 'Employer added successfully', 'success');
        
        // Pass the response to the parent component
        // For createEmployer, the response.data is an array
        const employerData = Array.isArray(response.data) ? response.data[0] : response.data;
        onSave(employerData);
        
        // Close modal immediately to prevent duplicate submissions
        resetForm();
        onClose();
      } 
      // Error case with validation errors
      else if (response.data && typeof response.data === 'object' && 'errors' in response.data) {
        fieldErrors = response.data.errors;
        handleFieldErrors(response.message || 'Validation failed');
      }
      // General error case
      else {
        showNotification(response.message || 'Failed to save employer', 'error');
      }
    } catch (error) {
      console.error("Error in employer form:", error);
      showNotification('An unexpected error occurred', 'error');
    } finally {
      loading = false;
    }
  }
</script>

{#if open}
  <div class="modal-overlay" role="presentation" on:click|self={handleCancel} on:keydown={(e) => e.key === 'Escape' && handleCancel()}>
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="add-employer-modal-title">
      <h2 id="add-employer-modal-title">{isEditMode ? 'Edit' : 'Add'} Employer</h2>

      <div class="form-section">
        <div class="form-group">
          <Input
            label="Company Name"
            required={true}
            bind:value={name}
            placeholder="Enter company name"
            fullWidth={true}
            error={getFieldError('name')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="ABN"
            required={true}
            bind:value={abn}
            placeholder="Enter ABN (11 digits)"
            fullWidth={true}
            error={getFieldError('abn')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Name"
            required={false}
            bind:value={contact_name}
            placeholder="Enter contact name"
            fullWidth={true}
            error={getFieldError('contact_name')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Email"
            required={false}
            bind:value={contact_email}
            placeholder="Enter contact email"
            fullWidth={true}
            type="email"
            error={getFieldError('contact_email')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Contact Phone"
            required={false}
            bind:value={contact_phone}
            placeholder="Enter contact phone"
            fullWidth={true}
            type="tel"
            error={getFieldError('contact_phone')}
          />
        </div>
        
        <div class="form-group">
          <Input
            label="Head Office Location"
            required={false}
            bind:value={head_office_location}
            placeholder="Enter head office location"
            fullWidth={true}
            error={getFieldError('head_office_location')}
          />
        </div>

        <div class="form-group">
          <label for="status">Status</label>
          <select 
            id="status" 
            bind:value={status}
            class="form-select"
            class:error={getFieldError('status')}
          >
            {#each statusOptions as option}
              <option value={option.uuid}>{option.name}</option>
            {/each}
          </select>
          {#if getFieldError('status')}
            <div class="field-error">{getFieldError('status')}</div>
          {/if}
        </div>
      </div>

      <div class="modal-actions">
        <Button 
          variant="primary" 
          on:click={handleSubmit}
          disabled={loading || !name.trim() || !abn.trim()}
          fullWidth={true}
        >
          {#if loading}
            <span class="loading-spinner"></span>
          {/if}
          {isEditMode ? 'Update' : 'Save'}
        </Button>
        <Button 
          variant="secondary" 
          on:click={handleCancel}
          disabled={loading}
          fullWidth={true}
        >
          Cancel
        </Button>
      </div>
    </div>
  </div>
{/if}

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  duration={notificationType === 'error' ? 0 : 3000}
/>

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
    padding: 1rem;
  }

  .modal {
    background-color: white;
    border-radius: 10px;
    padding: 2rem;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

  .modal h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
  }

  .form-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: white;
    color: #111827;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .form-select:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .form-select.error {
    border-color: #ef4444;
  }

  .field-error {
    font-size: 0.75rem;
    color: #ef4444;
    margin-top: 0.25rem;
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .loading-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid #ffffff;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    margin-right: 0.5rem;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .modal {
      padding: 1.5rem;
      max-width: 100%;
    }
  }
</style>

